/**
 * EmployeeTable Component
 * Table component for displaying employee list
 */

import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Box,
  TableSortLabel,
  Checkbox
} from '@mui/material';
import {
  Edit,
  Delete,
  Visibility,
  EventAvailable
} from '@mui/icons-material';
import { Employee } from '../../types/employee';
import { EmployeeAvatar } from './EmployeeAvatar';
import { EmployeeStatusChip } from './EmployeeStatusChip';
import { formatCPF } from '../../utils/employeeFormatters';
import { ControleIconButton, EmptyState } from '../common';
import type { ControleDeAcesso } from '../../authorization/useVeredito';

interface EmployeeTableProps {
  employees: Employee[];
  loading?: boolean;
  page: number;
  limit: number;
  total: number;
  sortBy: string;
  sortOrder: 'ASC' | 'DESC';
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onSortChange: (sortBy: string, sortOrder: 'ASC' | 'DESC') => void;
  onView?: (employee: Employee) => void;
  onEdit?: (employee: Employee) => void;
  onDelete?: (employee: Employee) => void;
  onScheduleAppointment?: (employee: Employee) => void;
  /**
   * O acesso a cada tela de destino, respondido por `useControle`. Vem de fora porque a
   * pergunta é sobre a tela para onde o ícone leva, e quem a conhece é a tela que monta a
   * tabela. Sem o veredito o ícone aparece habilitado: é a lista que sabe se ele age.
   */
  controleAgendar?: ControleDeAcesso;
  controleVer?: ControleDeAcesso;
  controleEditar?: ControleDeAcesso;
  controleExcluir?: ControleDeAcesso;
  selectable?: boolean;
  selectedIds?: number[];
  onSelectionChange?: (ids: number[]) => void;
  filtersActive?: boolean;
  onClearFilters?: () => void;
}

export const EmployeeTable: React.FC<EmployeeTableProps> = ({
  employees,
  loading = false,
  page,
  limit,
  total,
  sortBy,
  sortOrder,
  onPageChange,
  onLimitChange,
  onSortChange,
  onView,
  onEdit,
  onDelete,
  onScheduleAppointment,
  controleAgendar,
  controleVer,
  controleEditar,
  controleExcluir,
  selectable = false,
  selectedIds = [],
  onSelectionChange,
  filtersActive,
  onClearFilters
}) => {
  const handleSort = (field: string) => {
    if (sortBy === field) {
      onSortChange(field, sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      onSortChange(field, 'ASC');
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      onSelectionChange?.(employees.map(e => e.id));
    } else {
      onSelectionChange?.([]);
    }
  };

  const handleSelectOne = (id: number) => {
    const newSelection = selectedIds.includes(id)
      ? selectedIds.filter(selectedId => selectedId !== id)
      : [...selectedIds, id];
    onSelectionChange?.(newSelection);
  };

  const isAllSelected = employees.length > 0 && selectedIds.length === employees.length;
  const isSomeSelected = selectedIds.length > 0 && selectedIds.length < employees.length;

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {selectable && (
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isAllSelected}
                    indeterminate={isSomeSelected}
                    onChange={handleSelectAll}
                  />
                </TableCell>
              )}
              <TableCell>Funcionário</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'cpf'}
                  direction={sortBy === 'cpf' ? sortOrder.toLowerCase() as 'asc' | 'desc' : 'asc'}
                  onClick={() => handleSort('cpf')}
                >
                  CPF
                </TableSortLabel>
              </TableCell>
              <TableCell>Empresa</TableCell>
              <TableCell>Cargo</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {employees.length === 0 ? (
              <TableRow>
                <TableCell colSpan={selectable ? 7 : 6} align="center">
                  {loading ? (
                    'Carregando...'
                  ) : (
                    <EmptyState
                      filtersActive={filtersActive}
                      onClearFilters={onClearFilters}
                      config={{
                        title: 'Nenhum funcionário nesta empresa ainda',
                        description:
                          'Funcionários são a base dos exames e dos agendamentos. Cada um precisa de um cargo e um setor já cadastrados.',
                        prereqChain: 'employees',
                        action: {
                          label: 'Cadastrar funcionário',
                          node: 'employees',
                          deniedHint:
                            'Peça ao administrador da empresa para cadastrar um funcionário.',
                        },
                      }}
                    />
                  )}
                </TableCell>
              </TableRow>
            ) : (
              employees.map((employee) => (
                <TableRow
                  key={employee.id}
                  hover
                  selected={selectedIds.includes(employee.id)}
                >
                  {selectable && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.includes(employee.id)}
                        onChange={() => handleSelectOne(employee.id)}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <EmployeeAvatar
                        name={employee.name}
                        avatarUrl={null}
                        size="small"
                      />
                      <Box>
                        <Box fontWeight={600}>{employee.name}</Box>
                        {employee.socialName && (
                          <Box fontSize="0.75rem" color="text.secondary">
                            {employee.socialName}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{formatCPF(employee.cpf)}</TableCell>
                  <TableCell>{employee.company ? (employee.company.tradeName || employee.company.legalName) : '-'}</TableCell>
                  <TableCell>{employee.occupation?.name || '-'}</TableCell>
                  <TableCell>
                    <EmployeeStatusChip status={employee.status} />
                  </TableCell>
                  <TableCell align="right">
                    <Box display="flex" gap={0.5} justifyContent="flex-end">
                      {onView && (
                        <ControleIconButton
                          controle={controleVer ?? { disabled: false, tooltip: 'Visualizar' }}
                          size="small"
                          onClick={() => onView(employee)}
                        >
                          <Visibility fontSize="small" />
                        </ControleIconButton>
                      )}
                      {onScheduleAppointment && (
                        <ControleIconButton
                          controle={controleAgendar ?? { disabled: false, tooltip: 'Agendar Consulta' }}
                          size="small"
                          color="primary"
                          onClick={() => onScheduleAppointment(employee)}
                        >
                          <EventAvailable fontSize="small" />
                        </ControleIconButton>
                      )}
                      {onEdit && (
                        <ControleIconButton
                          controle={controleEditar ?? { disabled: false, tooltip: 'Editar' }}
                          size="small"
                          onClick={() => onEdit(employee)}
                        >
                          <Edit fontSize="small" />
                        </ControleIconButton>
                      )}
                      {onDelete && (
                        <ControleIconButton
                          controle={controleExcluir ?? { disabled: false, tooltip: 'Excluir' }}
                          size="small"
                          color="error"
                          onClick={() => onDelete(employee)}
                        >
                          <Delete fontSize="small" />
                        </ControleIconButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page - 1}
        onPageChange={(_, newPage) => onPageChange(newPage + 1)}
        rowsPerPage={limit}
        onRowsPerPageChange={(e) => onLimitChange(parseInt(e.target.value, 10))}
        rowsPerPageOptions={[10, 20, 50, 100]}
        labelRowsPerPage="Registros por página:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
      />
    </Paper>
  );
};
